import { baseApi } from "./baseApi";

const REPORT_BASE_URL = "/reports";

export const reportApi = baseApi.injectEndpoints({
  tagTypes: [
    "Report",
    "ShipmentPrice",
    "RegionSalesSummary",
    "RegionDayWiseSales",
    "BranchReport",
    "VendorReport",
    "Merchant",
  ],

  endpoints: (builder) => ({
    lateShipmentList: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/lateShipmentList`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Shipment",
              id: _id,
            })),
            { type: "Shipment", id: "LIST" },
          ]
          : [{ type: "Shipment", id: "LIST" }],
    }),
    misplacedShipmentList: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/misplacedShipments`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Shipment",
              id: _id,
            })),
            { type: "Shipment", id: "LIST" },
          ]
          : [{ type: "Shipment", id: "LIST" }],
    }),

    noPriceShipmentList: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/no-price-shipments`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Shipment",
              id: _id,
            })),
            { type: "Shipment", id: "LIST" },
          ]
          : [{ type: "Shipment", id: "LIST" }],
    }),

    getUniqueDestinationBranches: builder.query({
      query: ({ account, entity, status, keyword, types, lateShipment }) => ({
        url: `${REPORT_BASE_URL}/unique-destination-branches`,
        params: { account, entity, status, keyword, types, lateShipment },
      }),
    }),
    listShipmentPricesForReports: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/reports`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),
    listShipmentChargeReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/shipment-charge-report`,
        params,
      }),
      providesTags: [{ type: "ShipmentPrice", id: "CHARGE_REPORT" }],
    }),
    listEBillReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/ebill-report`,
        params,
      }),
      providesTags: [{ type: "Report", id: "EBILL_REPORT" }],
    }),
    listPendingPaymentsForReports: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/pending-payments-reports`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    downloadCOCCollectionReportExcel: builder.mutation({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/coc-report/excel`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
        cache: "no-cache",
      }),
      // No invalidatesTags needed since this is a download operation
    }),

    downloadDeliveryCollectionReportExcel: builder.mutation({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/delivery-report/excel`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
        cache: "no-cache",
      }),
      // No invalidatesTags needed since this is a download operation
    }),

    listCODReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/cod`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({ type: "Report", id: _id })),
            { type: "Report", id: "LIST" },
          ]
          : [{ type: "Report", id: "LIST" }],
    }),

    getVendorLeadTrends: builder.query({
      query: ({ months } = {}) => ({
        url: `${REPORT_BASE_URL}/analytics/trends`,
        params: { months },
      }),
      providesTags: ["VendorLeadTrends"],
    }),

    getVendorLeadTotalSummary: builder.query({
      query: () => ({
        url: `${REPORT_BASE_URL}/analytics/summary`,
      }),
      providesTags: ["VendorLeadTrends"],
    }),

    listGetDocumentsExpiry: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/getDocumentsExpiry`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({ type: "Report", id: _id })),
            { type: "Report", id: "LIST" },
          ]
          : [{ type: "Report", id: "LIST" }],
    }),

    listVendorPendingPaymentsPrint: builder.query({
      query: ({ vendorId, page, limit }) => ({
        url: `${REPORT_BASE_URL}/vendorPendingPayments/${vendorId}/vendorPrintPayment`,
        params: { page, limit },
      }),
      providesTags: (_result, _error, { vendorId }) => [
        { type: "VendorPendingPayment", id: vendorId },
        { type: "VendorPendingPayment", id: "LIST" },
      ],
    }),

    listVendorPendingPaymentsPrintForVendor: builder.query({
      query: ({ vendorId, page, limit }) => ({
        url: `${REPORT_BASE_URL}/vendorPendingPaymentsForVendor/${vendorId}/vendorPendingPaymentsForVendor`,
        params: { page, limit },
      }),
      providesTags: (_result, _error, { vendorId }) => [
        { type: "VendorPendingPayment", id: vendorId },
        { type: "VendorPendingPayment", id: "LIST" },
      ],
    }),

    listShipmentForBulkCancel: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/ShipmentListForBulkCancel`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({ type: "Report", id: _id })),
            { type: "Report", id: "LIST" },
          ]
          : [{ type: "Report", id: "LIST" }],
    }),

    listShipmentForReturned: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/ShipmentListForReturned`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({ type: "Report", id: _id })),
            { type: "Report", id: "LIST" },
          ]
          : [{ type: "Report", id: "LIST" }],
    }),

    downloadCODReportExcel: builder.mutation({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/cod/excel`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
        cache: "no-cache",
      }),
      // No invalidatesTags needed since this is a download operation
    }),

    getDDLVendorListReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/filter`,
        params,
      }),
      providesTags: () => [{ type: "Report", id: "LIST" }],
    }),
    listDailySalesReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/transaction-report`,
        params,
      }),
    }),
    downloadDailySalesReport: builder.mutation({
      query: ({
        fromDate,
        toDate,
        account,
        entity,
        branch,
        user,
        sortedBy,
        sortDirection,
        vendorId,
      }) => ({
        url: `${REPORT_BASE_URL}/transaction-report-download`,
        method: "GET",
        params: {
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
          ...(account && { account }),
          ...(entity && { entity }),
          ...(branch && branch !== "all" && { branch }),
          ...(user && user !== "all" && { user }),
          sortedBy,
          sortDirection,
          ...(vendorId && { vendorId }),
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    getDailySalesDetail: builder.query({
      query: ({
        date,
        account,
        entity,
        branch,
        user,
        vendorId,
        page,
        limit,
        tab,
        search,
      }) => ({
        url: `${REPORT_BASE_URL}/transaction-report/daily-detail`,
        params: {
          date,
          ...(account && { account }),
          ...(entity && { entity }),
          ...(branch && branch !== "all" && { branch }),
          ...(user && user !== "all" && { user }),
          ...(vendorId && { vendorId }),
          page,
          limit,
          tab,
          ...(search && { search }),
        },
      }),
    }),

    getVendorShipmentCounts: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/getVendorShipmentCounts`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result?.data?.map(({ _id }) => ({
              type: "VendorProfile",
              _id,
            })),
            { type: "VendorProfile", id: "LIST" },
          ]
          : [{ type: "VendorProfile", id: "LIST" }],
    }),

    getCOCUserReportDetail: builder.query({
      query: ({ userId, ...params }) => ({
        url: `${REPORT_BASE_URL}/coc-report/user/${userId}/detail`,
        params,
      }),
    }),

    getUserPendingPaymentReportDetail: builder.query({
      query: ({ userId, ...params }) => ({
        url: `${REPORT_BASE_URL}/cod-report/user/${userId}/detail`,
        params,
      }),
    }),

    getVendorTransactionReportDetail: builder.query({
      query: ({ vendorProfile, ...params }) => ({
        url: `${REPORT_BASE_URL}/vendor-transaction-report/vendor/${vendorProfile}/detail`,
        params,
      }),
    }),

    listUserShipmentPricesForReports: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/user/reports`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    listUserDeliveryPricesForReports: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/user/delivery-collection-reports`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    listVendorShipmentPricesForReports: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/vendor/transaction-reports`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    noVendorDocs: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/no-vendor-docs`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Shipment",
              id: _id,
            })),
            { type: "Shipment", id: "LIST" },
          ]
          : [{ type: "Shipment", id: "LIST" }],
    }),

    // Sales report endpoints
    getSalesReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/sales-report`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    // Sales summary endpoint
    getSalesSummary: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/sales-summary`,
        params,
      }),
    }),

    getDailySalesReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/sales-report/daily`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    getWeeklySalesReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/sales-report/weekly`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    getMonthlySalesReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/sales-report/monthly`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    downloadSalesReportExcel: builder.mutation({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/sales-report/excel`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
        cache: "no-cache",
      }),
    }),

    //regional sales report api slice
    getRegionSalesSummary: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/region-sales-summary`,
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "RegionSalesSummary",
              id: _id,
            })),
            { type: "RegionSalesSummary", id: "LIST" },
          ]
          : [{ type: "RegionSalesSummary", id: "LIST" }],
    }),

    getRegionDayWiseSales: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/region-day-wise-sales`,
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "RegionDayWiseSales",
              id: _id,
            })),
            { type: "RegionDayWiseSales", id: "LIST" },
          ]
          : [{ type: "RegionDayWiseSales", id: "LIST" }],
    }),

    //branch sales report
    getBranchSalesReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/branch-sales-report`,
        method: "GET",
        params,
      }),
      providesTags: ["BranchReport"],
    }),
    getVehicleMileageReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/vehicleMileageReport`,
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Shipment",
              id: _id,
            })),
            { type: "Shipment", id: "LIST" },
          ]
          : [{ type: "Shipment", id: "LIST" }],
    }),
    getMerchantReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/merchantReport`,
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({ type: "Merchant", id: _id })),
            { type: "Merchant", id: "LIST" },
          ]
          : [{ type: "Merchant", id: "LIST" }],
    }),

    downloadBranchSalesReportExcel: builder.mutation({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/branch-sales-report/export`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
      }),
    }),
    downloadRegionSalesReportExcel: builder.mutation({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/region-sales-report/export`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
      }),
    }),
    downloadRegionDayWiseSalesReportExcel: builder.mutation({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/region-daywise-sales-report/export`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
      }),
    }),
    getProvinceWeeklyAverageSales: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/province-weekly-average-sales`,
        method: "GET",
        params,
      }),
    }),

    vendorBranchReportList: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/vendor-branch-list`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Report",
              id: _id,
            })),
            { type: "Report", id: "LIST" },
          ]
          : [{ type: "Report", id: "LIST" }],
    }),
    getPriceChangeReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/price-change-report`,
        params: {
          ...params,
          page: params.page || 1,
          limit: params.limit || 10,
        },
      }),
      providesTags: (result) =>
        result?.data?.details && Array.isArray(result.data.details)
          ? [
            ...result.data.details.map(({ documentId }) => ({
              type: "ShipmentPrice",
              id: documentId?._id || "UNKNOWN",
            })),
            { type: "ShipmentPrice", id: "LOGS" },
          ]
          : [{ type: "ShipmentPrice", id: "LOGS" }],
    }),
    // Added Vendor Report Endpoints
    getVendorReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/vendor-report`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ vendorId }) => ({
              type: "VendorReport",
              id: vendorId,
            })),
            { type: "VendorReport", id: "LIST" },
          ]
          : [{ type: "VendorReport", id: "LIST" }],
    }),

    getVendorRMWiseReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/vendor-rm-report`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ vendorId }) => ({
              type: "VendorReport",
              id: vendorId,
            })),
            { type: "VendorReport", id: "LIST" },
          ]
          : [{ type: "VendorReport", id: "LIST" }],
    }),

    downloadVendorRMWiseReportExcel: builder.mutation({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/vendor-rm-report/export`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
      }),
    }),

    downloadVendorListByRMorOnboardedExcel: builder.mutation({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/vendor-list-by-rm-or-onboarded/export`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
      }),
    }),

    getDailyVendorReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/daily-vendor-report`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ vendorId }) => ({
              type: "VendorReport",
              id: vendorId,
            })),
            { type: "VendorReport", id: "LIST" },
          ]
          : [{ type: "VendorReport", id: "LIST" }],
    }),

    getWeeklyVendorReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/weekly-vendor-report`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ vendorId }) => ({
              type: "VendorReport",
              id: vendorId,
            })),
            { type: "VendorReport", id: "LIST" },
          ]
          : [{ type: "VendorReport", id: "LIST" }],
    }),

    getMonthlyVendorReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/monthly-vendor-report`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ vendorId }) => ({
              type: "VendorReport",
              id: vendorId,
            })),
            { type: "VendorReport", id: "LIST" },
          ]
          : [{ type: "VendorReport", id: "LIST" }],
    }),
    downloadVendorReportExcel: builder.mutation({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/vendor-report-download`,
        method: "GET",
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { blob };
        },
      }),
      cache: "no-cache",
    }),
    getVendorListByRMorOnboarded: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/vendor-list-by-rm-or-onboarded`,
        params,
      }),
      providesTags: (result) =>
        result?.data?.details
          ? [
            ...result.data.details.map(({ groupId }) => ({
              type: "VendorReport",
              id: groupId,
            })),
            { type: "VendorReport", id: "LIST" },
          ]
          : [{ type: "VendorReport", id: "LIST" }],
    }),

    getShipmentPricingReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/shipment-pricing`,
        params,
      }),
    }),
    listQRPaymentCollections: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/qr-payment`,
        params,
      }),
      providesTags: ["PaymentCollection"],
    }),

    // Average Delivery Time api

    getRegionAvgDeliveryTime: builder.query({
      query: ({ regionId, startDate, endDate }) => ({
        url: `${REPORT_BASE_URL}/averageDeliveryTime/region/${regionId}`,
        params: {
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      }),
    }),

    getBranchAvgDeliveryTime: builder.query({
      query: ({
        regionId,
        page,
        limit,
        keyword,
        startDate,
        endDate,
        exportAll,
      }) => ({
        url: `${REPORT_BASE_URL}/averageDeliveryTime/branch/${regionId}`,
        params: {
          page,
          limit,
          ...(keyword && { keyword }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(exportAll && { exportAll }),
        },
      }),
    }),
    getShipmentsByBranch: builder.query({
      query: ({
        branchId,
        page,
        limit,
        keyword,
        startDate,
        endDate,
        exportAll,
      }) => ({
        url: `${REPORT_BASE_URL}/shipments/branch/${branchId}`,
        params: {
          page,
          limit,
          ...(keyword && { keyword }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
          ...(exportAll && { exportAll }),
        },
      }),
    }),
    getResponsibleStats: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/responsible-stats`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lead", id: "RESPONSIBLE_STATS" }],
    }),

    getLeadsByResponsible: builder.query({
      query: ({ responsibleId, ...params }) => ({
        url: `${REPORT_BASE_URL}/responsible/${responsibleId}/breakdown`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, { responsibleId }) => [
        { type: "Lead", id: `RESPONSIBLE_${responsibleId}` },
      ],
    }),

    getResponsibleStatsExport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/responsible-stats/export`,
        method: "GET",
        params,
      }),
    }),

    getLeadsByResponsibleExport: builder.query({
      query: ({ responsibleId, ...params }) => ({
        url: `${REPORT_BASE_URL}/responsible/${responsibleId}/breakdown/export`,
        method: "GET",
        params,
      }),
    }),

    listCODChargeDeductReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/cod-charge-deduct`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({ type: "Report", id: _id })),
            { type: "Report", id: "LIST" },
          ]
          : [{ type: "Report", id: "LIST" }],
    }),

    // Branch COC Report
    getBranchCOCReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/prices/branch/coc-reports`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    // Province-level summary for Branch COC Report
    getBranchCOCProvinceReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/prices/branch/coc-province-reports`,
        params,
      }),
      providesTags: [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    getBranchCOCReportDetail: builder.query({
      query: ({ branchId, params }) => ({
        url: `${REPORT_BASE_URL}/coc-report/branch/${branchId}/detail`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    getBranchCOCUserSummary: builder.query({
      query: ({ branchId, params }) => ({
        url: `${REPORT_BASE_URL}/coc-report/branch/${branchId}/users`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),
    getBranchCOCVendorSummary: builder.query({
      query: ({ branchId, params }) => ({
        url: `${REPORT_BASE_URL}/coc-report/branch/${branchId}/vendors`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),
    getBranchCOCUserDetail: builder.query({
      query: ({ branchId, userId, params }) => ({
        url: `${REPORT_BASE_URL}/coc-report/branch/${branchId}/user/${userId}/detail`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),
    getBranchCOCVendorDetail: builder.query({
      query: ({ branchId, vendorId, params }) => ({
        url: `${REPORT_BASE_URL}/coc-report/branch/${branchId}/vendor/${vendorId}/detail`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    // Branch COD Report
    getBranchCODReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/prices/branch/cod-reports`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "ShipmentPrice",
              id: _id,
            })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    // Province-level summary for Branch COD Report
    getBranchCODProvinceReport: builder.query({
      query: ({ params }) => ({
        url: `${REPORT_BASE_URL}/prices/branch/cod-province-reports`,
        params,
      }),
      providesTags: [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    getBranchCODUserSummary: builder.query({
      query: ({ branchId, params }) => ({
        url: `${REPORT_BASE_URL}/cod-report/branch/${branchId}/users`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({ type: "ShipmentPrice", id: _id })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),
    getBranchCODUserDetail: builder.query({
      query: ({ branchId, userId, params }) => ({
        url: `${REPORT_BASE_URL}/cod-report/branch/${branchId}/user/${userId}/detail`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({ type: "ShipmentPrice", id: _id })),
            { type: "ShipmentPrice", id: "LIST" },
          ]
          : [{ type: "ShipmentPrice", id: "LIST" }],
    }),

    // Isolated merged pending COD + COC branch report
    getMergedBranchProvinceReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/branch/coc-cod-province-reports`,
        params,
      }),
      providesTags: [{ type: "ShipmentPrice", id: "MERGED_BRANCH_LIST" }],
    }),
    getMergedBranchReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/branch/coc-cod-reports`,
        params,
      }),
      providesTags: [{ type: "ShipmentPrice", id: "MERGED_BRANCH_LIST" }],
    }),
    getMergedBranchResponsibleReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/branch/coc-cod-responsible-reports`,
        params,
      }),
      providesTags: [{ type: "ShipmentPrice", id: "MERGED_BRANCH_LIST" }],
    }),
    getMergedBranchShipmentReport: builder.query({
      query: (params) => ({
        url: `${REPORT_BASE_URL}/prices/branch/coc-cod-shipment-reports`,
        params,
      }),
      providesTags: [{ type: "ShipmentPrice", id: "MERGED_BRANCH_LIST" }],
    }),

  }),
  overrideExisting: false,
});

export const {
  useListLateShipmentListQuery,
  useLazyLateShipmentListQuery,
  useLazyMisplacedShipmentListQuery,
  useLazyNoPriceShipmentListQuery,
  useLazyGetUniqueDestinationBranchesQuery,
  useLazyListShipmentPricesForReportsQuery,
  useLazyListShipmentChargeReportQuery,
  useLazyListEBillReportQuery,
  useDownloadCOCCollectionReportExcelMutation,
  useDownloadDeliveryCollectionReportExcelMutation,
  useLazyListCODReportQuery,
  useLazyListGetDocumentsExpiryQuery,
  useLazyListShipmentForBulkCancelQuery,
  useLazyListShipmentForReturnedQuery,
  useDownloadCODReportExcelMutation,
  useLazyGetDDLVendorListReportQuery,
  useLazyListDailySalesReportQuery,
  useDownloadDailySalesReportMutation,
  useLazyGetDailySalesDetailQuery,

  useLazyListUserShipmentPricesForReportsQuery,

  useLazyGetCOCUserReportDetailQuery,
  useLazyListUserDeliveryPricesForReportsQuery,

  useLazyGetUserPendingPaymentReportDetailQuery,
  useLazyGetVendorTransactionReportDetailQuery,
  useLazyGetVendorShipmentCountsQuery,
  useLazyListVendorShipmentPricesForReportsQuery,
  useLazyNoVendorDocsQuery,
  //sales report hooks
  useGetSalesReportQuery,
  useLazyGetSalesReportQuery,
  useGetDailySalesReportQuery,
  useLazyGetDailySalesReportQuery,
  useGetWeeklySalesReportQuery,
  useLazyGetWeeklySalesReportQuery,
  useGetMonthlySalesReportQuery,
  useLazyGetMonthlySalesReportQuery,
  useDownloadSalesReportExcelMutation,
  useGetSalesSummaryQuery,
  useLazyGetSalesSummaryQuery,
  useLazyGetRegionSalesSummaryQuery,
  useLazyGetRegionDayWiseSalesQuery,
  useDownloadRegionSalesReportExcelMutation,
  useDownloadRegionDayWiseSalesReportExcelMutation,
  useLazyGetProvinceWeeklyAverageSalesQuery,
  useLazyGetBranchSalesReportQuery,
  useLazyGetVehicleMileageReportQuery,
  useGetMerchantReportQuery,
  useLazyGetMerchantReportQuery,
  useDownloadBranchSalesReportExcelMutation,
  useLazyVendorBranchReportListQuery,
  useLazyListPendingPaymentsForReportsQuery,

  //price update hook list
  useGetPriceChangeReportQuery,
  useLazyListVendorPendingPaymentsPrintQuery,
  useLazyListVendorPendingPaymentsPrintForVendorQuery,

  // vendor report
  useGetVendorReportQuery,
  useLazyGetVendorReportQuery,
  useLazyGetVendorRMWiseReportQuery,
  useDownloadVendorRMWiseReportExcelMutation,
  useDownloadVendorListByRMorOnboardedExcelMutation,
  useLazyGetVendorListByRMorOnboardedQuery,
  useGetDailyVendorReportQuery,
  useLazyGetDailyVendorReportQuery,
  useGetWeeklyVendorReportQuery,
  useLazyGetWeeklyVendorReportQuery,
  useGetMonthlyVendorReportQuery,
  useLazyGetMonthlyVendorReportQuery,
  useDownloadVendorReportExcelMutation,

  useLazyGetShipmentPricingReportQuery,

  // QR Payment List
  useLazyListQRPaymentCollectionsQuery,

  // Average Delivery Time api query
  useLazyGetRegionAvgDeliveryTimeQuery,
  useLazyGetBranchAvgDeliveryTimeQuery,
  useLazyGetShipmentsByBranchQuery,

  useLazyGetResponsibleStatsQuery,
  useLazyGetLeadsByResponsibleQuery,
  useLazyGetResponsibleStatsExportQuery,
  useLazyGetLeadsByResponsibleExportQuery,

  useLazyListCODChargeDeductReportQuery,

  // For Branch COD and COC
  useLazyGetBranchCOCReportQuery,
  useLazyGetBranchCOCProvinceReportQuery,
  useLazyGetBranchCOCUserSummaryQuery,
  useLazyGetBranchCOCVendorSummaryQuery,
  useLazyGetBranchCOCUserDetailQuery,
  useLazyGetBranchCOCVendorDetailQuery,
  useLazyGetBranchCODReportQuery,
  useLazyGetBranchCODProvinceReportQuery,
  useLazyGetBranchCODUserSummaryQuery,
  useLazyGetBranchCODUserDetailQuery,
  useLazyGetMergedBranchProvinceReportQuery,
  useLazyGetMergedBranchReportQuery,
  useLazyGetMergedBranchResponsibleReportQuery,
  useLazyGetMergedBranchShipmentReportQuery,

  //Vendor Lead Trends
  useGetVendorLeadTrendsQuery,
  useLazyGetVendorLeadTotalSummaryQuery,
  useLazyGetVendorLeadTrendsQuery,
} = reportApi;
